/**
 * The tenant search box — a combobox (WAI-ARIA 1.2 pattern): type a unit number or a name, results
 * appear as you type; ↑/↓ move through them, Enter opens, Esc clears. The number of results is
 * announced. Used on Look up and in the header (shortcut: "/").
 */
import { forwardRef, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantSummary } from '@shared/api-types';
import type { ProgramKey } from '@shared/people';
import { useTenantSearch } from '@/lib/queries';
import { useAnnounce } from './Announcer';
import { Icon } from './Icons';
import { cn } from '@/lib/utils';

export interface TenantSearchProps {
  onOpen: (t: TenantSummary) => void;
  /** Enter with nothing highlighted (the header sends you to Look up with the text). */
  onSubmitText?: (q: string) => void;
  initial?: string;
  compact?: boolean;
  label: string;
  placeholder: string;
  id?: string;
  autoFocus?: boolean;
  testId?: string;
  /** 1.5.9: the program this search is for — its enrollees first, the others marked "not enrolled". */
  program?: ProgramKey;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(h);
  }, [value, ms]);
  return v;
}

export const TenantSearch = forwardRef<HTMLInputElement, TenantSearchProps>(function TenantSearch(
  { onOpen, onSubmitText, initial = '', compact, label, placeholder, id, autoFocus, testId, program },
  ref,
) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const uid = useId();
  const inputId = id ?? `search-${uid}`;
  const listId = `${inputId}-list`;
  const [text, setText] = useState(initial);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const q = useDebounced(text, 120);
  const res = useTenantSearch(q, program);
  const results = q.trim() && res.data && res.data.q.toLowerCase() === q.trim().toLowerCase() ? res.data.tenants : [];
  // The list on screen answers exactly what is in the box (not a search still catching up with typing).
  const fresh = !!res.data && !res.isFetching && res.data.q.toLowerCase() === text.trim().toLowerCase();
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => setText(initial), [initial]);

  // Announce the count once per settled query.
  const last = useRef('');
  useEffect(() => {
    if (!q.trim() || !res.data || res.isFetching) return;
    const key = `${res.data.q}|${res.data.tenants.length}`;
    if (last.current === key) return;
    last.current = key;
    announce(res.data.tenants.length ? t('lookup.resultsCount', { count: res.data.tenants.length }) : t('lookup.noResults'));
  }, [q, res.data, res.isFetching, announce, t]);

  useEffect(() => setActive(results.length ? 0 : -1), [results.length, q]);

  const choose = (tn: TenantSummary) => {
    setOpen(false);
    onOpen(tn);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (results.length ? (a + 1) % results.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (results.length ? (a - 1 + results.length) % results.length : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!fresh) {
        if (onSubmitText && text.trim()) onSubmitText(text.trim());
        return;
      }
      const pick = results[active];
      if (pick) choose(pick);
      else if (onSubmitText && text.trim()) onSubmitText(text.trim());
    } else if (e.key === 'Escape') {
      if (text || open) {
        e.preventDefault();
        e.stopPropagation();
      }
      setText('');
      setOpen(false);
    }
  };

  useEffect(() => {
    const onDown = (ev: MouseEvent) => {
      if (!wrap.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const expanded = open && !!text.trim();
  return (
    <div className={cn('tsearch', compact && 'compact')} ref={wrap} data-testid={testId}>
      <label className="sr-only" htmlFor={inputId}>
        {label}
      </label>
      <Icon name="lookup" className="tsearch-icon" aria-hidden="true" />
      <input
        ref={ref}
        id={inputId}
        className="input"
        type="search"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={expanded && active >= 0 && results[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      <ul id={listId} role="listbox" aria-label={t('lookup.results')} aria-busy={expanded && !fresh} className="tsearch-list" hidden={!expanded}>
        {expanded && results.length === 0 && !res.isFetching && res.data ? (
          <li className="tsearch-empty" role="presentation">
            {t('lookup.noResults')}
          </li>
        ) : null}
        {results.map((tn, i) => (
          <li
            key={tn.tenantId}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === active}
            className={i === active ? 'active' : undefined}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(tn)}
            onMouseEnter={() => setActive(i)}
          >
            <b className="tnum">{tn.unit}</b>
            <span>{tn.preferredName ? `${tn.preferredName} (${tn.name})` : tn.name}</span>
            {tn.category === 'Staff' ? <span className="help">{t('status.staff')}</span> : null}
            {tn.status !== 'Active' ? <span className="help">{t(`tenantStatus.${tn.status}`, { defaultValue: tn.status })}</span> : null}
            {tn.enrolled === false && tn.status === 'Active' ? <span className="help">{t('lookup.notEnrolled')}</span> : null}
            {tn.paused ? (
              <span className="status hold" data-testid="search-paused">
                {t('pause.label')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
});
